import type { EnvironmentId, GitBranch } from "@cmmd-forge/contracts";
import { Schema } from "effect";
import { resolveProductEnvironmentLabel } from "~/productSafeLabels";
export {
  dedupeRemoteBranchesWithLocalMatches,
  deriveLocalBranchNameFromRemoteRef,
} from "@cmmd-forge/shared/git";

const EnvMode = Schema.Literals(["local", "worktree"]);
export type EnvMode = typeof EnvMode.Type;

export function resolveEnvironmentOptionLabel(input: {
  isPrimary: boolean;
  environmentId: EnvironmentId;
  runtimeLabel?: string | null;
  savedLabel?: string | null;
}): string {
  return resolveProductEnvironmentLabel(input);
}

export function resolveEnvModeLabel(mode: EnvMode): string {
  return mode === "worktree" ? "New worktree" : "Current checkout";
}

export function resolveCurrentWorkspaceLabel(activeWorktreePath: string | null): string {
  return activeWorktreePath ? "Current worktree" : resolveEnvModeLabel("local");
}

export function resolveLockedWorkspaceLabel(activeWorktreePath: string | null): string {
  return activeWorktreePath ? "Worktree" : "Local checkout";
}

export function resolveWorkspaceTriggerLabel(input: { activeWorktreePath: string | null }): string {
  return input.activeWorktreePath ? "Worktree" : "Project Folder";
}

export function getBranchTriggerLabel(input: {
  activeWorktreePath: string | null;
  effectiveEnvMode: EnvMode;
  resolvedActiveBranch: string | null;
}): string {
  const { activeWorktreePath, effectiveEnvMode, resolvedActiveBranch } = input;
  if (!resolvedActiveBranch) {
    return "Select branch";
  }
  if (effectiveEnvMode === "worktree" && !activeWorktreePath) {
    return `From ${resolvedActiveBranch}`;
  }
  return resolvedActiveBranch;
}

export function buildBranchPickerItems(input: {
  branchNames: readonly string[];
  checkoutPullRequestItemValue: string | null;
  createBranchItemValue: string | null;
  hasExactBranchMatch: boolean;
}): string[] {
  const { branchNames, checkoutPullRequestItemValue, createBranchItemValue, hasExactBranchMatch } =
    input;
  const items = [...branchNames];
  if (createBranchItemValue && !hasExactBranchMatch) {
    items.push(createBranchItemValue);
  }
  if (checkoutPullRequestItemValue) {
    items.unshift(checkoutPullRequestItemValue);
  }
  return items;
}

export function getBranchStatusText(input: {
  branchCount: number;
  hasNextPage: boolean;
  isBranchesSearchPending: boolean;
  isFetchingNextPage: boolean;
  totalBranchCount: number;
}): string | null {
  const {
    branchCount,
    hasNextPage,
    isBranchesSearchPending,
    isFetchingNextPage,
    totalBranchCount,
  } = input;
  if (isBranchesSearchPending) {
    return "Loading branches...";
  }
  if (isFetchingNextPage) {
    return "Loading more branches...";
  }
  if (hasNextPage) {
    return `Showing ${branchCount} of ${totalBranchCount} branches`;
  }
  return null;
}

export function resolveBranchPickerItemBadge(input: {
  activeProjectCwd: string | null;
  branch: Pick<GitBranch, "current" | "isDefault" | "isRemote" | "worktreePath">;
}): "current" | "worktree" | "remote" | "default" | null {
  const { activeProjectCwd, branch } = input;
  const hasSecondaryWorktree =
    branch.worktreePath !== null &&
    activeProjectCwd !== null &&
    branch.worktreePath !== activeProjectCwd;
  if (branch.current) {
    return "current";
  }
  if (hasSecondaryWorktree) {
    return "worktree";
  }
  if (branch.isRemote) {
    return "remote";
  }
  if (branch.isDefault) {
    return "default";
  }
  return null;
}

export function resolveEffectiveEnvMode(input: {
  activeWorktreePath: string | null;
  hasServerThread: boolean;
  draftThreadEnvMode: EnvMode | undefined;
}): EnvMode {
  const { activeWorktreePath, hasServerThread, draftThreadEnvMode } = input;
  if (!hasServerThread) {
    if (activeWorktreePath) {
      return "local";
    }
    return draftThreadEnvMode === "worktree" ? "worktree" : "local";
  }
  return activeWorktreePath ? "worktree" : "local";
}

export function resolveDraftEnvModeAfterBranchChange(input: {
  nextWorktreePath: string | null;
  currentWorktreePath: string | null;
  effectiveEnvMode: EnvMode;
}): EnvMode {
  const { nextWorktreePath, currentWorktreePath, effectiveEnvMode } = input;
  if (nextWorktreePath) {
    return "worktree";
  }
  if (effectiveEnvMode === "worktree" && !currentWorktreePath) {
    return "worktree";
  }
  return "local";
}

export function resolveBranchToolbarValue(input: {
  envMode: EnvMode;
  activeWorktreePath: string | null;
  activeThreadBranch: string | null;
  currentGitBranch: string | null;
}): string | null {
  const { envMode, activeWorktreePath, activeThreadBranch, currentGitBranch } = input;
  if (envMode === "worktree" && !activeWorktreePath) {
    return activeThreadBranch ?? currentGitBranch;
  }
  return currentGitBranch ?? activeThreadBranch;
}

export function resolveBranchSelectionTarget(input: {
  activeProjectCwd: string;
  activeWorktreePath: string | null;
  branch: Pick<GitBranch, "isDefault" | "worktreePath">;
}): {
  checkoutCwd: string;
  nextWorktreePath: string | null;
  reuseExistingWorktree: boolean;
} {
  const { activeProjectCwd, activeWorktreePath, branch } = input;

  if (branch.worktreePath) {
    return {
      checkoutCwd: branch.worktreePath,
      nextWorktreePath: branch.worktreePath === activeProjectCwd ? null : branch.worktreePath,
      reuseExistingWorktree: true,
    };
  }

  const nextWorktreePath =
    activeWorktreePath !== null && branch.isDefault ? null : activeWorktreePath;

  return {
    checkoutCwd: nextWorktreePath ?? activeProjectCwd,
    nextWorktreePath,
    reuseExistingWorktree: false,
  };
}

/**
 * The name a ref would be created with, given what the user typed.
 *
 * A git ref cannot contain whitespace, so a query containing any can only ever
 * have meant the dashed form. Ported from t3code, where the same rule names a
 * newly created branch — which is what makes it the right rule to *search*
 * with too: it is the name the branch would already have.
 */
export function sanitizeNewRefName(rawName: string): string {
  return rawName.trim().replace(/[ \t\n\r\f\v]+/g, "-");
}

export function shouldIncludeBranchPickerItem(input: {
  itemValue: string;
  normalizedQuery: string;
  createBranchItemValue: string | null;
  checkoutPullRequestItemValue: string | null;
}): boolean {
  const { itemValue, normalizedQuery, createBranchItemValue, checkoutPullRequestItemValue } = input;

  if (normalizedQuery.length === 0) {
    return true;
  }

  if (createBranchItemValue && itemValue === createBranchItemValue) {
    return true;
  }

  if (checkoutPullRequestItemValue && itemValue === checkoutPullRequestItemValue) {
    return true;
  }

  const lowerItemValue = itemValue.toLowerCase();
  if (lowerItemValue.includes(normalizedQuery)) {
    return true;
  }

  // A query containing whitespace can only ever match a ref under its sanitized
  // name, because that is the name such a ref would have been created with.
  // Without this, typing "new branch" hides an existing "new-branch". Forge's
  // copy of this function was missing the block; the live picker path in
  // `filterBranchesBySearch` had the same hole and the same symptom.
  const sanitizedQuery = sanitizeNewRefName(normalizedQuery);
  return (
    sanitizedQuery.length > 0 &&
    sanitizedQuery !== normalizedQuery &&
    lowerItemValue.includes(sanitizedQuery)
  );
}

/**
 * "The thread's branch is not the one you have checked out."
 *
 * Only meaningful for threads working directly in the project checkout: a
 * worktree thread owns its own directory, so its branch is by construction the
 * one checked out there and can never drift from it. Ported from t3code, which
 * uses the same predicate to warn on its sidebar hover panel.
 *
 * Returns the pair rather than a boolean so the caller can name both branches.
 */
export function resolveLocalCheckoutBranchMismatch(input: {
  effectiveEnvMode: EnvMode;
  activeWorktreePath: string | null;
  activeThreadBranch: string | null;
  currentGitBranch: string | null;
}): { threadBranch: string; currentBranch: string } | null {
  const { effectiveEnvMode, activeWorktreePath, activeThreadBranch, currentGitBranch } = input;
  if (effectiveEnvMode !== "local" || activeWorktreePath !== null) {
    return null;
  }
  if (!activeThreadBranch || !currentGitBranch || activeThreadBranch === currentGitBranch) {
    return null;
  }
  return { threadBranch: activeThreadBranch, currentBranch: currentGitBranch };
}

/**
 * The branch to offer when the typed name is not already in the list.
 *
 * Selecting a branch on a draft is choosing a *name* for the thread to start
 * from — `useDraftBranchSelection` holds it in React state and no git call
 * happens until the thread runs. So offering an unrecognised name is safe, and
 * without it the picker is a dead end: `knownBranches` is only `main` plus the
 * project default, so typing `develop` filters the list to nothing with no way
 * forward.
 *
 * Returns the *sanitized* name, because that is what a ref typed with spaces
 * would actually be called — the same rule `filterBranchesBySearch` searches by
 * since FOR-2581, so the option and the search agree about what you typed.
 *
 * Null when there is nothing to offer: an empty query, or a name already in the
 * list. Offering an existing branch would render it twice.
 */
export function resolveTypedBranchOption(input: {
  readonly query: string;
  readonly knownBranches: readonly string[];
}): string | null {
  const name = sanitizeNewRefName(input.query);
  if (name.length === 0) return null;
  const lowered = name.toLowerCase();
  if (input.knownBranches.some((branch) => branch.toLowerCase() === lowered)) return null;
  return name;
}
